// Money rendering — ONE formatter, so the money lock has one place to bite.
//
// Before this file there were 34 local `rand()` / `fmtR()` / `money()` helpers
// scattered across the app and 123 inline `R ${…}` renders, in three different
// shapes. That is why the lock hangs here: rewriting 34 definitions to delegate
// covers 233 call sites at once, and every future one inherits the masking for
// free instead of quietly leaking.
//
// MASKED VALUES MUST NOT LOOK LIKE NUMBERS. `R •••` reads as "hidden"; `R 0.00`
// or `R —` reads as "zero", and an operator who believes a wage is zero will
// act on it. That distinction is the whole reason this isn't a CSS blur.

import { useCallback } from 'react'
import { useMoneyLock, isMoneyLockedNow } from '@/lib/moneyLock'
import { formatFixed } from '@/lib/numberFormat'

export interface MoneyOpts {
  /** 2 for exact figures (wages, invoices); 0 for rolled-up totals. */
  decimals?: 0 | 2
  /** Include the `R ` prefix. Off inside a column already headed "R". */
  prefix?:   boolean
}

/** What a hidden value renders as. Deliberately not a number. */
export const MONEY_MASK = '•••'

/**
 * Format a rand amount. PURE — never masks. Use for PDFs, CSVs and payslips
 * generated by someone who has already passed the lock; use `useMoney()` for
 * anything rendered on screen.
 */
export function fmtMoney(n: number | null | undefined, opts: MoneyOpts = {}): string {
  const { decimals = 2, prefix = true } = opts
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return prefix ? 'R —' : '—'
  const body = formatFixed(Number(n), decimals)
  return prefix ? `R ${body}` : body
}



/**
 * Masking formatter for MODULE-LEVEL helpers, which can't call a hook.
 *
 * Prefer `useMoney()` or `<Money/>` in anything that renders inside a
 * component — they subscribe properly. Reach for this only where the existing
 * helper is declared at module level (typically because module-level row
 * functions in a print sheet use it); the one-line delegation
 *
 *   const fmtR = (n: number) => fmtMoneyAuto(n)
 *
 * then masks every one of its call sites at once. See the note on
 * `isMoneyLockedNow()` for how the re-render happens.
 */
export function fmtMoneyAuto(n: number | null | undefined, opts: MoneyOpts = {}): string {
  if (isMoneyLockedNow()) return opts.prefix === false ? MONEY_MASK : `R ${MONEY_MASK}`
  return fmtMoney(n, opts)
}

/**
 * The screen formatter: identical to `fmtMoney` while unlocked, `R •••` while
 * locked. `locked` is returned alongside so a caller can also hide a whole
 * column, disable a CSV export, or skip a chart axis rather than render a row
 * of dots.
 */
export function useMoney(): { locked: boolean; money: (n: number | null | undefined, opts?: MoneyOpts) => string } {
  const { locked } = useMoneyLock()
  const money = useCallback((n: number | null | undefined, opts: MoneyOpts = {}) => {
    if (locked) return opts.prefix === false ? MONEY_MASK : `R ${MONEY_MASK}`
    return fmtMoney(n, opts)
  }, [locked])
  return { locked, money }
}

/**
 * Component form, for the inline `R ${…}` sites. `tabular-nums` keeps a column
 * from jumping around as values mask and unmask.
 */
export function Money({ value, decimals, prefix, className }: {
  value:      number | null | undefined
  decimals?:  0 | 2
  prefix?:    boolean
  className?: string
}) {
  const { locked, money } = useMoney()
  return (
    <span
      className={`tabular-nums ${className ?? ''}`}
      // Screen readers and hover both get the honest reason, rather than a
      // string of bullets with no explanation.
      title={locked ? 'Hidden — unlock money to view' : undefined}
      aria-label={locked ? 'Hidden' : undefined}
    >
      {money(value, { decimals, prefix })}
    </span>
  )
}

/**
 * Add one rand amount to a running total, where a NULL means "the database
 * withheld it" (piece-work earnings are NULL for a reader who may not see pay,
 * mig 20261148500000). A NULL input makes the total NULL, so the screen prints
 * "—" and a CSV leaves the cell blank. `Number(x) || 0` for money turns a
 * withheld figure into a confident R0.00; use this instead. `undefined` counts
 * as 0, as it always did (an absent optional field is not a withheld one).
 */
export function addZar(total: number | null, v: number | null | undefined): number | null {
  if (total === null || v === null) return null
  return total + (Number(v) || 0)
}
